import { useRecoilValue } from 'recoil';

import * as K from 'common/constants';
import { labsState } from 'components/account/account.state';

import Link from 'components/common/Link/Link';

import { HELP_ROUTE } from './constants';
import { NavLink } from './HelpPage';
import HelpSearchBox from './HelpSearchBox';
import './HelpPage.scss';

type HelpItem = {
  text: string;
  link: string;
  lab?: K.LAB;
};

type HelpSection = {
  title: string;
  items: HelpItem[];
};

const HELP_SECTIONS: HelpSection[] = [
  {
    title: 'Getting Started',
    items: [
      { text: 'Welcome!', link: '/help/welcome' },
      { text: 'Overview', link: '/help/getting-started' },
      { text: 'Integrations', link: '/help/solutions' },
      { text: 'Tips + Tricks', link: '/help/tips' },
    ],
  },
  {
    title: 'Agent Installation',
    items: [
      { text: 'Linux', link: '/help/install-agent-linux-quick-start-1' },
      { text: 'Docker', link: '/help/install-agent-docker' },
      { text: 'ECS', link: '/help/install-agent-ecs' },
      { text: 'Kubernetes', link: '/help/install-agent-kubernetes' },
      { text: 'Helm', link: '/help/install-agent-kubernetes-helm' },
      { text: 'Windows', link: '/help/install-agent-windows' },
    ],
  },
  {
    title: 'Agent Configuration',
    items: [
      { text: 'The Agent', link: '/help/scalyr-agent' },
      { text: 'Docker', link: '/help/scalyr-agent-docker' },
      { text: 'Kubernetes', link: '/help/scalyr-agent-k8s' },
      { text: 'Kubernetes Explorer', link: '/help/scalyr-agent-k8s-explorer' },
      { text: 'Env-Aware Agent Vars', link: '/help/scalyr-agent-env-aware' },
      { text: 'Increase Agent Throughput', link: '/help/scalyr-agent-increase-throughput' },
    ],
  },
  {
    title: 'Integrations',
    items: [
      { text: 'Get Data In', link: '/help/solutions' },
      { text: 'Add an HTTP Monitor', link: '/help/monitors' },
      { text: 'Create an Agent Plugin', link: '/help/creating-a-monitor-plugin' },
    ],
  },
  {
    title: 'Parsing and Processing Logs',
    items: [
      { text: 'Parsers', link: '/help/parsing-logs' },
      { text: 'Data Scrubbing', link: '/help/log-processing' },
      { text: 'Mask or Delete Accidental Logs', link: '/help/mask-and-delete-sensitive-logs' },
    ],
  },
  {
    title: 'RBAC',
    items: [
      { text: 'Manage Users', link: '/help/users' },
      { text: 'Manage Teams', link: '/help/teams' },
    ],
  },
  {
    title: 'The ' + K.PRODUCT_NAME + ' UI',
    items: [
      { text: 'The Account Menu', link: '/help/account-menu' },
      { text: 'Search', link: '/help/log-overview' },
      { text: 'PowerQueries', link: '/help/power-queries' },
      { text: 'Graphs', link: '/help/graphs' },
      { text: 'Dashboards', link: '/help/dashboards' },
      { text: 'Alerts', link: '/help/alerts' },
    ],
  },
  {
    title: 'View / Analyze',
    items: [
      { text: 'SmartLinks', link: '/help/smart-links' },
      { text: 'Saved Searches', link: '/help/saved-searches' },
      { text: 'Logs', link: '/help/view' },
      { text: 'Query Language', link: '/help/query-language' },
      { text: 'Search Helper', link: '/help/search-helper' },
      { text: 'Regex', link: '/help/regex' },
      { text: 'Time Reference', link: '/help/time-reference' },
      { text: 'System Requirements', link: '/help/system-requirements' },
    ],
  },
  {
    title: 'Other Topics',
    items: [
      { text: 'Configuration Files', link: '/help/config' },
      { text: 'Export and Archive to S3', link: '/help/exports' },
      { text: 'How ' + K.PRODUCT_NAME + ' Works', link: '/help/how-scalyr-works' },
      { text: 'Timeout Tips', link: '/help/timeout-tips' },
      { text: 'Post to Slack', link: '/help/post-to-slack' },
      { text: 'Hindsight', link: '/help/hindsight', lab: K.LAB.HINDSIGHT },
    ],
  },
  {
    title: 'API',
    items: [
      { text: 'API Keys', link: '/help/api-keys' },
      { text: K.PRODUCT_NAME + ' API', link: '/help/api' },
      { text: 'Java API', link: '/help/java-api' },
      { text: 'Command Line', link: '/help/command-line' },
    ],
  },
];

type HelpNavProps = {
  path: HELP_ROUTE;
  navLinks?: NavLink[];
};

/**
 * The left-side navigation panel for help pages. Contains links to all help pages, organized into sections.
 *
 * @prop path        the current help route
 * @prop navLinks    a set of links to show in the sub-menu for the current help page
 */
function HelpNav({ path, navLinks }: HelpNavProps): JSX.Element {
  const labs = useRecoilValue(labsState);
  const enabledLabs = new Set(labs.filter(({ enabled }) => enabled).map(({ name }) => name));

  const sections = HELP_SECTIONS.map((helpSection, sectionIndex) => {
    const items = helpSection.items.map((item, itemIndex) => {
      if (item.lab && !enabledLabs.has(item.lab)) {
        return null;
      }
      let subMenu = null;
      // Display a sub-menu (expand current item) if we got nav links
      if (path === item.link && navLinks && navLinks.length > 0) {
        const subItems = navLinks.map((navLink, navLinkIndex) => (
          <li className="HelpPage__Nav__SubItem" key={['help', 'nav', sectionIndex, itemIndex, navLinkIndex].join('-')}>
            <Link href={path + '#' + navLink.name} className="HelpPage__Nav__Link">
              {navLink.text}
            </Link>
          </li>
        ));
        subMenu = <ul>{subItems}</ul>;
      }
      return (
        <span key={['help', 'nav', sectionIndex, itemIndex].join('-')}>
          <li>
            <Link href={item.link} className="HelpPage__Nav__Link">
              {item.text}
            </Link>
          </li>
          {subMenu}
        </span>
      );
    });

    return (
      <li className="HelpPage__Nav__Section" key={['help', 'nav', sectionIndex].join('-')}>
        <span className="HelpPage__Nav__Title">{helpSection.title}</span>
        <ul className="HelpPage__Nav__Items">{items}</ul>
      </li>
    );
  });

  return (
    <div className="HelpPage__Nav">
      <ul>
        <li className="HelpPage__Nav__Items">
          <HelpSearchBox />
        </li>
        {sections}
      </ul>
    </div>
  );
}

export default HelpNav;
